// Dashboard (HomeScreen) layout + the design system's "hs-" components: the
// two-column grid, weekly-streak card, subject item rows (homeworks/support-assignments),
// interactive calendar, and announcement rows. Ported from the design system's
// ui_kits/learner HomeScreen (the Welcome banner lives in WelcomeStyles).
// Prefix: hs-.
export const homeScreenStyles = `
  .hs { display: flex; flex-direction: column; gap: var(--space-6); }

  /* top row: banner + streak — same column ratio as .hs-cols so the hero lines
     up exactly with the Ödevler column below it (right edges align, no jog) */
  .hs-top { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr); gap: var(--space-6); align-items: stretch; }

  /* two-column layout */
  .hs-cols { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr); gap: var(--space-6); align-items: start; }
  .hs-col { display: flex; flex-direction: column; gap: var(--space-6); min-width: 0; }

  /* glass card shell (matches the design Card "glass") */
  .hs-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5);
    border-radius: var(--rad-catchup-large);
    /* Solid (no blur) — several of these cards stack on the scrolling
       dashboard; blur only frosts the flat page bg (pure raster cost). */
    background: var(--glass-flat-solid);
    border: 1px solid var(--glass-flat-border);
    box-shadow: var(--glass-flat-edge), var(--glass-flat-float-rest);
  }
  .hs-card__head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); }
  .hs-card__title { display: inline-flex; align-items: center; gap: var(--space-2); font-size: var(--fs-2xl); font-weight: var(--font-weight-bold); color: var(--text-primary); }
  .hs-card__title img { width: 20px; height: 20px; object-fit: contain; }
  .hs-link { border: none; background: none; cursor: pointer; font-family: var(--font-sans); font-size: var(--fs-base); font-weight: var(--font-weight-bold); color: var(--catchup-blue-600); text-decoration: none; white-space: nowrap; transition: opacity var(--duration-base) var(--ease-standard); }
  .hs-link:hover { opacity: 0.7; }
  .hs-points { display: inline-flex; align-items: center; gap: 5px; font-size: var(--fs-base); font-weight: var(--font-weight-bold); color: var(--accent-gold-text);
    background: var(--accent-gold-bg); border: 1px solid var(--accent-gold-border); padding: 4px 12px; border-radius: var(--rad-catchup-full); }
  .hs-points img { width: 16px; height: 16px; }

  .hs-bar { height: 7px; border-radius: 99px; background: rgba(90,155,170,0.18); overflow: hidden; }
  .hs-bar__fill { height: 100%; border-radius: 99px; box-shadow: inset 0 1px 1px rgba(255,255,255,0.4); transition: width var(--duration-slow) var(--ease-standard); }

  /* status tags (reuse accent palette) */
  .hs-tag { font-size: var(--fs-sm); font-weight: var(--font-weight-bold); padding: 2px 9px; border-radius: var(--rad-catchup-full); border: 1px solid transparent; white-space: nowrap; display: inline-flex; align-items: center; gap: 5px; }
  .hs-tag::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
  .hs-tag--ongoing { background: var(--brand-50); border-color: var(--color-info-border); color: var(--brand-dark); }
  .hs-tag--new { background: var(--accent-coral-bg); border-color: var(--accent-coral-border); color: var(--accent-coral-text); }
  .hs-tag--live { background: var(--accent-coral-bg); border-color: var(--accent-coral-border); color: var(--accent-coral-text); }
  .hs-tag--done { background: var(--color-success-bg); border-color: var(--color-success-border); color: var(--color-success-text); }
  .hs-tag--scheduled { background: var(--brand-50); border-color: var(--color-info-border); color: var(--brand-dark); }

  /* weekly streak / status card */
  .hs-streak__hero { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-3) var(--space-4); border-radius: var(--rad-catchup-large);
    background: linear-gradient(120deg, var(--accent-coral-bg), rgba(245,166,35,0.1)); border: 1px solid var(--accent-coral-border); }
  .hs-streak__stat { display: flex; align-items: center; gap: var(--space-2); flex: 1 1 0; min-width: 0; }
  .hs-streak__ico { font-size: 30px; line-height: 1; flex-shrink: 0; filter: drop-shadow(0 3px 6px rgba(0,0,0,0.12)); }
  .hs-streak__txt { display: flex; flex-direction: column; min-width: 0; }
  .hs-streak__sep { align-self: stretch; width: 1px; background: var(--accent-coral-border); margin: 4px 2px; }
  .hs-streak__count { font-size: var(--fs-2xl); font-weight: var(--font-weight-bold); color: var(--text-primary); line-height: 1; }
  .hs-streak__sub { font-size: var(--fs-md); font-weight: var(--font-weight-semi); color: var(--accent-coral-text); margin-top: 3px; }
  .hs-streak__week { display: flex; justify-content: space-between; gap: 4px; }
  .hs-streak__day { display: flex; flex-direction: column; align-items: center; gap: 5px; flex: 1; }
  .hs-streak__dot { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    background: rgba(90,155,170,0.12); border: 1.5px solid transparent; font-size: 15px; }
  .hs-streak__dot img { width: 18px; }
  .hs-streak__dot.is-done { background: var(--accent-lime-bg); }
  .hs-streak__dot.is-today { background: var(--accent-coral-bg); border-color: var(--accent-coral); box-shadow: 0 0 0 3px rgba(249,128,102,0.15); }
  .hs-streak__lbl { font-size: var(--fs-sm); font-weight: var(--font-weight-semi); color: var(--text-muted); }
  .hs-streak__lbl.is-today { color: var(--accent-coral-text); font-weight: var(--font-weight-bold); }

  /* report stats */
  .hs-report__stats { display: flex; gap: var(--space-3); }
  .hs-stat { flex: 1; display: flex; flex-direction: column; gap: 2px; padding: var(--space-3) var(--space-4); border-radius: var(--rad-catchup-large);
    background: rgba(255,255,255,0.5); border: 1px solid rgba(255,255,255,0.7); box-shadow: var(--glass-flat-edge), inset 0 1px 1px rgba(255,255,255,0.5); }
  .hs-stat__num { font-size: var(--fs-great); font-weight: var(--font-weight-bold); color: var(--text-primary); line-height: 1; }
  .hs-stat__lbl { font-size: var(--fs-md); font-weight: var(--font-weight-semi); color: var(--text-muted); }

  /* calendar — extracted to the shared library <Calendar/> primitive (cal- prefix) */

  @media (max-width: 1080px) {
    .hs-cols { grid-template-columns: 1fr; }
    .hs-top { grid-template-columns: 1fr; }
  }
  @media (max-width: 560px) {
    .hs-report__stats { flex-direction: column; }
  }
`;
